import React, { useState } from "react";
import { Field } from "formik";
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import ContactPopup from "./ContactPopup";
import {
  AdminPanelTitle,
  AdminMetric,
  AdminMetadataText,
  AdminTableBody,
  AdminMainText,
} from "../../sections/AdminDashboard/shared/adminUx";

const ContactWidget = ({ setFieldValue, values, errors }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(0);

  // Define fields based on the image provided
  const fields = [
    { name: "Mobile number", key: "mobileNumber", estimatedTime: "2 min", required: true },
    { name: "Landline number", key: "phoneNumber", estimatedTime: "2 min", required: false },
    { name: "Business email", key: "businessEmail", estimatedTime: "2 min", required: true },
    { name: "Business website", key: "website", estimatedTime: "3 min", required: false },
    { name: "Google Business Profile", key: "googleBusinessProfile", estimatedTime: "3 min", required: false }
  ];

  // No sample data - all fields start empty
  const displayValues = {
    mobileNumber: values.mobileNumber || "",
    phoneNumber: values.phoneNumber || "",
    businessEmail: values.businessEmail || "",
    website: values.website || "",
    googleBusinessProfile: values.googleBusinessProfile || ""
  };

  // Calculate completion percentage
  const calculateProgress = () => {
    const completedFields = fields.filter(field => {
      // Check if field has a value
      const hasValue = displayValues[field.key] && displayValues[field.key].toString().trim() !== "";
      return hasValue;
    }).length;
    
    return Math.round((completedFields / fields.length) * 100);
  };

  // Calculate estimated time for remaining fields
  const calculateEstimatedTime = () => {
    const remainingFields = fields.filter(field => {
      const hasValue = displayValues[field.key] && displayValues[field.key].toString().trim() !== "";
      return !hasValue;
    });
    
    const totalMinutes = remainingFields.reduce((total, field) => {
      const timeStr = field.estimatedTime;
      const minutes = parseInt(timeStr.replace(' min', ''));
      return total + minutes;
    }, 0);
    
    return totalMinutes > 0 ? `Estimated ${totalMinutes} min` : 'All Completed';
  };

  const progress = calculateProgress();
  const estimatedTime = calculateEstimatedTime();

  return (
    <>
      <DashboardMainContainer className="masonry-item w-[500px] h-auto account-widget">
        <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
          <AdminPanelTitle className="text-offblack">Business Contact</AdminPanelTitle>
          <div className="flex items-center gap-2">
            <div className="w-16 h-1.5 bg-lightgray rounded-full overflow-hidden">
              <div 
                className="h-full bg-freshgreen transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <AdminMetric size="micro" className="text-offblack tabular-nums">
              {progress}%
            </AdminMetric>
          </div>
        </div>
        
        <div className="px-4 py-4">
          {/* Fields List */}
          <div className="space-y-0">
            {fields.map((field, index) => {
              const hasValue = displayValues[field.key] && displayValues[field.key].toString().trim() !== "";
              const isCompleted = hasValue;
              
              return (
                <div key={field.key} className="flex items-center justify-between">
            {progress === 100 ? (
              <div 
                className={`group flex items-center w-full py-[4px] cursor-pointer hover:text-primaryblue rounded px-2 -mx-2`}
                onClick={() => {
                  setSelectedFieldIndex(index);
                  setIsPopupOpen(true);
                }}
              >
                <AdminTableBody emphasis className="w-56 text-offblack group-hover:text-primaryblue">
                  {field.name}
                </AdminTableBody>
                <AdminMainText className="text-offblack group-hover:text-primaryblue truncate">
                  {isCompleted ? displayValues[field.key] : `Estimated ${field.estimatedTime}`}
                </AdminMainText>
              </div>
            ) : (
                    <>
                      <div 
                        className={`group flex items-center py-[4px] cursor-pointer hover:text-primaryblue rounded px-2 -mx-2 ${index < fields.length - 1 ? 'border-b border-transparent' : ''}`}
                        onClick={() => {
                          setSelectedFieldIndex(index);
                          setIsPopupOpen(true);
                        }}
                      >
                        <div className={`w-2 h-2 rounded-full mr-3 ${isCompleted ? 'bg-freshgreen' : 'bg-mediumgray'}`}></div>
                        <AdminTableBody emphasis className={`w-40 ${isCompleted ? 'text-offblack group-hover:text-primaryblue' : 'text-mediumgray group-hover:text-primaryblue'}`}>
                          {field.name}
                        </AdminTableBody>
                        <AdminMainText className={`text-offblack group-hover:text-primaryblue truncate max-w-[200px]`}>
                          {isCompleted ? displayValues[field.key] : ''}
                        </AdminMainText>
                      </div>
                      <AdminMetadataText className="text-mediumgray">
                        {isCompleted ? 'Completed' : 'Start'}
                      </AdminMetadataText>
                    </>
                  )}
                </div>
              );
            })}
          </div>

        </div>
      </DashboardMainContainer>

      {/* Popup for editing */}
      {isPopupOpen && (
        <ContactPopup
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          setFieldValue={setFieldValue}
          values={values}
          errors={errors}
          initialStep={selectedFieldIndex}
        />
      )}
    </>
  );
};

export default ContactWidget;
